import { ChevronRight } from 'lucide-react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerTitle,
} from '@rocco/components/drawer';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import StockAndShipping from '@modules/product/component/variant-purchase/StockAndShipping';
import type Variant from '@modules/product/type/variant';
import type ProductSize from '@modules/product/type/product-size';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { MobileVariantSelector } from './MobileVariantSelector';
import {
    InlineAddToCartButton,
    InlineSamplePurchaseButton,
    PILL,
    PurchasePriceBlock,
    QuantityStepper,
    SampleShippingInfo,
} from './purchase-flow-parts';

export interface MobileVariantSheetProps {
    variants: Variant[];
    /** Sizes actually used by a variant (the legacy `sizesInUse` filter). */
    sizes: ProductSize[];
    isBuyable: boolean;
    isSampleBuyable: boolean;
    /** Selected-variant price for the pinned footer — the sheet covers the
     *  page's price block, and the pick changes the price. Null hides the row. */
    priceString: string | null;
    isPriceRange: boolean;
    isOnSale: boolean;
    retailPriceString: string | null;
    pricePrefix: string | null;
    /** Whether the professional accepts enquiries (audience gating). Gates the
     *  "Enquire about this configuration" footer action. */
    showCTA: boolean;
    /** Controlled sheet visibility — the summary rows and the shop banner share
     *  one sheet, so the parent owns whether it is open. */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** A summary row (or the shop banner) was tapped — the parent opens the sheet. */
    onSpecify: () => void;
    /** Opens the enquiry modal (the sheet closes itself first). */
    onEnquire: () => void;
    /** Fires after a successful add to cart — the sheet closes itself so the
     *  caller can present the added-to-cart confirmation. */
    onAdded: () => void;
}

interface SummaryRow {
    key: string;
    label: string;
    /** Current pick, or the placeholder when nothing is chosen yet. */
    value: string;
    /** True while the row shows the "Select" placeholder (renders grey). */
    placeholder: boolean;
    /** Colour rows preview the picked swatch beside its name. */
    swatch?: { image?: string | null; code?: string | null };
}

/**
 * The collapsed mobile variant picker: one summary row per option group
 * ("Colour — Black ›") with the full {@link MobileVariantSelector} in a bottom
 * sheet behind any row tap. Opening a row signals purchase intent, so the
 * sheet completes the transaction in place: selector, stock and shipping
 * cost/time, then the real commerce CTA with the quantity stepper pinned at
 * the bottom (Add to Cart / sample purchase / the enquiry CTA) — no dead-end
 * "Done". Requires the variant option/selection providers AND the shipping
 * providers above it. Renders nothing when there is nothing to configure.
 */
export const MobileVariantSheet = ({
    variants,
    sizes,
    isBuyable,
    isSampleBuyable,
    priceString,
    isPriceRange,
    isOnSale,
    retailPriceString,
    pricePrefix,
    showCTA,
    open,
    onOpenChange,
    onSpecify,
    onEnquire,
    onAdded,
}: MobileVariantSheetProps) => {
    const { product } = useLoaderData<typeof loader>();
    const { attributeMap, decision } = useVariantOptionState();

    const attributes = [...attributeMap.values()];
    // Nothing to configure and nothing to transact → render nothing. A buyable
    // (or sample-buyable) product with no options still needs the sheet: the
    // shop banner opens it for the quantity + Add to Cart / Order Sample footer.
    const hasOptions = attributes.length > 0 || sizes.length > 0;
    if (!hasOptions && !isBuyable && !isSampleBuyable) return null;

    // Mirrors MobileVariantSelector: a product whose variants all carry a size
    // has no "Not Applicable" state — nothing picked yet just means "Select".
    const sizeIsGeneric =
        variants.length === 0 || variants.every((v) => Boolean(v.Size));

    const rows: SummaryRow[] = attributes.map((attribute) => {
        const selectedId = decision.options[attribute.ID] ?? 0;
        const option = attribute.options.find((o) => o.ID === selectedId);
        const placeholder = !option && attribute.isGeneric;
        return {
            key: `attribute-${attribute.ID}`,
            label: attribute.Name,
            value: option?.Label ?? (placeholder ? 'Select' : 'Not Applicable'),
            placeholder,
            // Only when there is something to show: the label beside it
            // already names the colour, and a colourway with neither a
            // texture nor a hex drew as a blank chip.
            swatch:
                attribute.ID === ProductAttributeEnum.Colour &&
                option &&
                (option.Image?.Desktop || option.Code)
                    ? {
                          image: option.Image?.Desktop,
                          code: option.Code,
                      }
                    : undefined,
        };
    });
    if (sizes.length > 0) {
        const selectedSize = sizes.find((size) => size.ID === decision.size);
        const placeholder = !selectedSize && sizeIsGeneric;
        rows.push({
            key: 'size',
            label: 'Size',
            value:
                selectedSize?.FullTitle ??
                (placeholder ? 'Select' : 'Not Applicable'),
            placeholder,
        });
    }

    return (
        <>
            <div className="[&:empty]:hidden">
                {rows.map((row) => (
                    <button
                        key={row.key}
                        type="button"
                        onClick={onSpecify}
                        className="flex w-full items-center justify-between gap-4 border-b border-black/10 py-3.5 text-left first:border-t"
                    >
                        <span className="shrink-0 font-sans text-sm text-[#7f7f7f]">
                            {row.label}
                        </span>
                        <span
                            className={cn(
                                'flex min-w-0 items-center gap-2 font-sans text-sm',
                                row.placeholder
                                    ? 'text-[#767676]'
                                    : 'text-[#181818]',
                            )}
                        >
                            {row.swatch ? (
                                <span
                                    aria-hidden="true"
                                    className="size-4 shrink-0 rounded-full border border-black/10 bg-cover bg-center"
                                    style={
                                        row.swatch.image
                                            ? {
                                                  backgroundImage: `url(${row.swatch.image})`,
                                              }
                                            : {
                                                  backgroundColor:
                                                      row.swatch.code ??
                                                      '#f1f1f1',
                                              }
                                    }
                                />
                            ) : null}
                            <span className="truncate">{row.value}</span>
                            <ChevronRight className="size-4 shrink-0 text-[#767676]" />
                        </span>
                    </button>
                ))}
            </div>

            <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
                {/* noHandle: the handle slot otherwise mounts a stray pill
                    button over the sheet; swipe-down and the X still close. */}
                <DrawerContent noHandle>
                    <DrawerDescription className="hidden" aria-hidden />
                    {/* The height cap lives on this column (not the vaul
                        content, whose h-auto breaks percentage heights): the
                        selector area scrolls inside it, the footer stays
                        pinned. 4.75rem matches the mobile header height. */}
                    <div className="flex max-h-[calc(100dvh-4.75rem)] min-h-0 flex-col">
                        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pt-5 pb-4">
                            <DrawerTitle className="pr-10 text-left font-sans text-xl leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]">
                                {product.Title}
                            </DrawerTitle>
                            {hasOptions ? (
                                <div className="mt-5">
                                    <MobileVariantSelector
                                        variants={variants}
                                        sizes={sizes}
                                    />
                                </div>
                            ) : null}
                            {/* The conversion-critical facts: stock, shipping
                                cost and delivery time for the selection. */}
                            {isBuyable ? (
                                <div className="mt-6">
                                    <StockAndShipping
                                        refundPolicyData={product}
                                    />
                                </div>
                            ) : null}
                            {!isBuyable && isSampleBuyable ? (
                                <div className="mt-6">
                                    <SampleShippingInfo />
                                </div>
                            ) : null}
                        </div>

                        {/* Pinned footer, one consistent order however the sheet
                            was opened: the selection's live price (the pick
                            changes it, and the sheet covers the page's price
                            block), then "Enquire about this configuration"
                            (the user just told us exactly what they want — the
                            strongest lead on the page), with commerce (quantity +
                            Add to Cart / Order Sample) as the outlined secondary.
                            Commerce only leads when enquiries are off. */}
                        <div className="flex shrink-0 flex-col gap-3 border-t border-black/10 px-4 pt-4 pb-6">
                            <PurchasePriceBlock
                                align="start"
                                compact
                                priceString={priceString}
                                isPriceRange={isPriceRange}
                                isInServiceableArea={
                                    product.IsInServiceableArea
                                }
                                isOnSale={isOnSale}
                                retailPriceString={retailPriceString}
                                pricePrefix={pricePrefix}
                            />
                            {showCTA ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        onOpenChange(false);
                                        onEnquire();
                                    }}
                                    className={PILL('filled-light')}
                                >
                                    Enquire about this configuration
                                </button>
                            ) : null}
                            {isBuyable ? (
                                <div className="flex items-center gap-3">
                                    <QuantityStepper />
                                    <div className="min-w-0 flex-1">
                                        <InlineAddToCartButton
                                            openCartPopup={false}
                                            variant={
                                                showCTA
                                                    ? 'outlined-light'
                                                    : 'filled-light'
                                            }
                                            onAdded={() => {
                                                onOpenChange(false);
                                                onAdded();
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null}
                            {isSampleBuyable ? (
                                <InlineSamplePurchaseButton
                                    openCartPopup={false}
                                    onAdded={() => {
                                        onOpenChange(false);
                                        onAdded();
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
};
